import Link from "next/link";
import { useEffect, useState } from "react";
import clsx from "clsx";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider } from "react-hook-form";

import { useExtendedForm } from "@chef/hooks";
import {
  api,
  BillingQuery,
  getStore,
  TimeblocksQuery,
  useLazyTimeblocksByRangeWithGeoQuery,
  useTimeblocksQuery,
} from "@chef/state-management";
import { Button, Checkbox, Dropdown, Input, Hr } from "@chef/components";
import { BRAND_NAME, BRAND_PATHS, TIMEBLOCK_TYPE_IDS } from "@chef/constants";
import {
  isNightTimeDelivery,
  isValidPostalCode,
  padPostalCode,
} from "@chef/helpers";
import {
  AddressForm as AddressValidationForm,
  DigitalKeyProviderInfo,
} from "@chef/smart-components";
import { Info } from "@chef/icons/small";
import { isNotEmptyArray } from "@chef/utils/array";
import { isEqualStrings } from "@chef/utils/equal";

import {
  createDeliveryFrequencyOptions,
  createDeliveryTimeslotOptions,
} from "../utils";

import { intl } from "./AddressForm.Intl";

interface AddressFormProps {
  initialAddress?: BillingQuery["billing"]["addresses"][number];
  onFormSubmitted: (values: AddressFormValues) => void;
  isLoading?: boolean;

  // Pass these values to enable editing of delivery details
  initialTimeblockId?: number;
  initialBasketDeliveryWeekType?: number;
}

interface SelectDeliveryInformationProps {
  basketTimeblockId: number;
  onBasketTimeblockIdChanged: (update: number) => void;

  basketDeliveryWeekType: number;
  onBasketDeliveryWeekTypeChanged: (update: number) => void;

  timeblocks: TimeblocksQuery["timeblocksData"]["timeblocks"];
}

export const AddressForm = ({
  initialAddress,
  initialTimeblockId,
  initialBasketDeliveryWeekType,
  onFormSubmitted,
  isLoading,
}: AddressFormProps) => {
  const paddedPostalCode = initialAddress?.postalCode
    ? padPostalCode(initialAddress.postalCode.toString())
    : undefined;

  const methods = useExtendedForm<AddressFormValues>({
    defaultValues: {
      streetAddress: initialAddress?.address,
      postalCode: paddedPostalCode,
      instructionsToDriver: initialAddress?.instructionsToDriver || "",
      doorCode: initialAddress?.doorCode || "",
      name: initialAddress?.name,
      requiresPhysicalKey: false,
      basketTimeblockId: initialTimeblockId,
      basketDeliveryWeekType: initialBasketDeliveryWeekType,
      setAsNewShippingAddress: false,
      validatedAddress: {
        digitalKeyProvider: {
          name: initialAddress?.digitalKeyProviderName,
        },
      },
      apartment: {},
    },
    resolver: yupResolver(schema),
    mode: "all",
  });
  const { register, handleSubmit, setValue, formValues } = methods;

  const {
    state: postalCodeLookup,
    cannotDeliver,
    defaultTimeblockId,
  } = useCityForPostalCode(
    formValues.validatedAddress?.postalCode,
    formValues.validatedAddress?.latitude,
    formValues.validatedAddress?.longitude,
  );

  const hasTimeblock = !!formValues.basketTimeblockId;
  const basketTimeblockId = hasTimeblock
    ? formValues.basketTimeblockId
    : defaultTimeblockId;

  const postalCode =
    formValues.postalCode || formValues.validatedAddress?.postalCode;

  const { data: timeblocksDataQuery } = useTimeblocksQuery(
    {
      postalCode: postalCode || "",
      timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
    },
    {
      skip: !postalCode,
    },
  );

  const timeblocks = timeblocksDataQuery?.timeblocksData?.timeblocks;

  const selectedTimeblock = timeblocks?.find(
    (timeblock) => timeblock.timeblockId === basketTimeblockId,
  );

  const nightTimeDeliverySelected = isNightTimeDelivery(selectedTimeblock);

  const apartments = formValues.validatedAddress?.apartments;

  const digitalKeyProvider =
    formValues.validatedAddress?.digitalKeyProvider?.name;

  const hasDigitalKeyProvider = isEqualStrings(
    digitalKeyProvider?.toLowerCase(),
    "bekey",
  );

  const isApartmentAddress = isNotEmptyArray(apartments ?? []);

  const shouldShowNightDeliveryNotification =
    !hasDigitalKeyProvider && isApartmentAddress && nightTimeDeliverySelected;

  useEffect(() => {
    if (initialAddress) {
      return;
    }
    if (postalCodeLookup?.type !== "success") {
      return;
    }
    if (!formValues.setAsNewShippingAddress) {
      setValue("basketTimeblockId", undefined);
      setValue("basketDeliveryWeekType", undefined);
      return;
    }
    setValue("basketTimeblockId", defaultTimeblockId);
    setValue("basketDeliveryWeekType", 10);
  }, [formValues.setAsNewShippingAddress]);

  useEffect(() => {
    if (postalCodeLookup?.type === "success") {
      setValue("postalArea", postalCodeLookup.city);
    }
  }, [postalCodeLookup, setValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmitted)}>
        <div className="flex justify-between">
          <h2 className="text-xl">
            <strong>{intl.ADDRESS_INFORMATION}</strong>
          </h2>
          {initialAddress?.isShipping && (
            <div className="mb-1 text-sm text-white ">
              <div className="px-1 rounded-sm bg-secondary">
                <strong>{intl.ACTIVE_ADDRESS}</strong>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-8 mt-6">
          {initialAddress && (
            <section>
              <p>
                <strong>{intl.DID_YOU_MOVE}</strong>
              </p>
              <div className="flex gap-1 text-sm">
                <p>{intl.YOU_HAVE_TO}</p>
                <Link
                  passHref
                  href={BRAND_PATHS.ADD_NEW_ADDRESS_PAGE}
                  className="underline text-primary"
                >
                  {intl.ADD_NEW_ADDRESS}
                </Link>
              </div>
            </section>
          )}

          <AddressValidationForm
            initialAddress={
              initialAddress
                ? {
                    streetName:
                      initialAddress.streetName || initialAddress.address,
                    streetNumber: initialAddress.streetNumber
                      ? initialAddress.streetNumber.toString()
                      : "",
                    letterExtra: initialAddress.letterExtra,
                    postalCode:
                      paddedPostalCode || initialAddress.postalCode.toString(),
                    postalArea: initialAddress.city,
                    floorNumber: initialAddress?.floorNumber,
                    door: initialAddress?.flatNo,
                    apartmentName: initialAddress?.apartmentName,
                    validated: initialAddress.validated,
                  }
                : undefined
            }
            doNotDeliver={cannotDeliver}
            locked={!!initialAddress}
          />

          {postalCodeLookup?.type === "success" && !initialAddress && (
            <div
              className={clsx(
                "flex flex-col p-4 border-2 border-transparent",
                formValues.setAsNewShippingAddress &&
                  "bg-informationBG border-information border-2",
                !formValues.setAsNewShippingAddress && "bg-background",
              )}
            >
              <div className="flex items-center">
                <Checkbox
                  {...register("setAsNewShippingAddress")}
                  id="setAsNewShippingAddress"
                  className="bg-white"
                />
                <label
                  className="max-w-sm ml-3 text-base cursor-pointer"
                  htmlFor="setAsNewShippingAddress"
                >
                  <strong>{intl.USE_AS_ACTIVE_ADDRESS_LABEL}</strong>
                </label>
              </div>
            </div>
          )}

          <Hr light />

          <h2 className="text-lg">{intl.DELIVERY_DETAILS}</h2>

          <Input label={intl.ADDRESS_NAME} id="alias" {...register("name")} />

          <Input
            label={intl.INSTRUCTIONS_TO_DRIVER}
            id="instructionsToDriver"
            {...register("instructionsToDriver")}
            helperText={intl.OPTIONAL_HELPER_TEXT}
          />

          <Input
            label={intl.DOOR_CODE}
            id="doorCode"
            {...register("doorCode")}
            helperText={intl.OPTIONAL_HELPER_TEXT}
          />
        </div>

        <DigitalKeyProviderInfo
          digitalKeyProvider={
            formValues.validatedAddress?.digitalKeyProvider?.name
          }
        />

        {BRAND_NAME === "RN" &&
          shouldShowNightDeliveryNotification &&
          postalCodeLookup?.type === "success" && (
            <div className="p-4 mb-8 mt-4 text-sm border-1.5 rounded-md bg-highlightBG border-highlight">
              <h3 className="flex items-center gap-2 mb-2 text-sm">
                <Info className="text-highlight" width={18} height={18} />
                <strong>Bemærk venligst!</strong>
              </h3>
              <p>
                Natlevering kan betyde, at din måltidskasse bliver sat udenfor
                uden opsyn i længere tid.
              </p>
            </div>
          )}

        {formValues.setAsNewShippingAddress &&
          basketTimeblockId &&
          timeblocks && (
            <SelectDeliveryInformation
              basketTimeblockId={basketTimeblockId}
              onBasketTimeblockIdChanged={(value) =>
                setValue("basketTimeblockId", value)
              }
              basketDeliveryWeekType={formValues.basketDeliveryWeekType || 10}
              onBasketDeliveryWeekTypeChanged={(value) =>
                setValue("basketDeliveryWeekType", value)
              }
              timeblocks={timeblocks}
            />
          )}

        <div className="flex flex-col gap-4 mt-10 md:flex-row-reverse">
          <Button primary loading={isLoading} id="save-changes-btn">
            {intl.SAVE_BUTTON_TEXT}
          </Button>

          <Link
            passHref
            href={BRAND_PATHS.DELIVERY_INFORMATION_PAGE}
            id="cancel-btn"
            legacyBehavior
          >
            <Button disabled={isLoading} outlined>
              {intl.CANCEL_BUTTON_TEXT}
            </Button>
          </Link>
        </div>
      </form>
    </FormProvider>
  );
};

const SelectDeliveryInformation = ({
  basketTimeblockId,
  onBasketTimeblockIdChanged,

  basketDeliveryWeekType,
  onBasketDeliveryWeekTypeChanged,

  timeblocks,
}: SelectDeliveryInformationProps) => {
  const deliveryTimeslotOptions = createDeliveryTimeslotOptions(
    timeblocks || [],
  );

  const deliveryFrequencyOptions = createDeliveryFrequencyOptions();

  return (
    <>
      <Hr light className="mt-10" />
      <div className="flex flex-col gap-6 mt-10">
        <h2 className="text-xl">
          <strong>{intl.DELIVERY}</strong>
        </h2>
        <div>
          <label className="text-sm">{intl.WHEN_TO_DELIVER}:</label>
          <Dropdown
            value={basketTimeblockId}
            onChange={onBasketTimeblockIdChanged}
            options={deliveryTimeslotOptions}
            placeholder={intl.LOADING_IN_PLACEHOLDER}
          />
        </div>

        <div>
          <label className="text-sm">{intl.HOW_OFTEN_TO_DELIVER}:</label>
          <Dropdown
            value={basketDeliveryWeekType}
            onChange={onBasketDeliveryWeekTypeChanged}
            options={deliveryFrequencyOptions}
          />
        </div>
      </div>
    </>
  );
};

const useCityForPostalCode = (
  postalCode?: string,
  latitude?: number,
  longitude?: number,
) => {
  const [getTimeblocksByRangeWithGeo] = useLazyTimeblocksByRangeWithGeoQuery();

  const [defaultTimeblockId, setDefaultTimeblockId] = useState<number>();

  const [state, setState] = useState<
    | { type: "no-city-found" }
    | { type: "success"; city: string }
    | { type: "loading" }
    | { type: "invalid-postal-code" }
    | { type: "no-coordinates-provided" }
    | { type: "address-outside-geofence" }
  >();

  useEffect(() => {
    if (!isValidPostalCode(postalCode)) {
      setState({ type: "invalid-postal-code" });
      return;
    }

    // We will update this logic later when we have coordinates for all addresses
    if (!latitude || !longitude) {
      setState({ type: "no-coordinates-provided" });
      // return;
    }

    getTimeblocksByRangeWithGeo({
      postalCode: postalCode || "",
      timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
      range: 4,
      latitude,
      longitude,
    })
      .unwrap()
      .then((data) => {
        if (
          data.timeblocksByRangeWithGeo.isGeoRestricted &&
          !data.timeblocksByRangeWithGeo.deliveryAvailable
        ) {
          setState({
            type: "address-outside-geofence",
          });

          return;
        }

        const city =
          data.timeblocksByRangeWithGeo.weeks[0]?.timeblocks[0]?.city;

        const defaultTimeblockId =
          data.timeblocksByRangeWithGeo.weeks[0]?.timeblocks[0]?.timeblockId;

        if (!city) {
          setState({
            type: "no-city-found",
          });

          return;
        }

        setDefaultTimeblockId(defaultTimeblockId);
        setState({
          type: "success",
          city,
        });
      });
  }, [getTimeblocksByRangeWithGeo, postalCode, latitude, longitude]);

  return {
    state,
    cannotDeliver:
      state?.type === "no-city-found" ||
      state?.type === "address-outside-geofence",
    defaultTimeblockId,
  };
};

const schema = yup.object({
  streetAddress: yup.string().required(),
  postalArea: yup.string().required(),
  // postalCode test can possibly be removed, does not apply to new address form
  postalCode: yup
    .string()
    .matches(/^[0-9]*$/, intl.CAN_ONLY_CONTAIN_NUMBERS)
    .length(
      intl.LENGTH_OF_POSTAL_CODE,
      intl.MUST_BE_EXACTLY_DIGITS(intl.LENGTH_OF_POSTAL_CODE),
    )
    .test({
      test: async (postalCode) => {
        if (
          postalCode === undefined ||
          postalCode.length !== intl.LENGTH_OF_POSTAL_CODE
        ) {
          return true;
        }

        try {
          const timeblocksByRangeWithGeoQuery = await getStore()
            .dispatch(
              api.endpoints.timeblocksByRangeWithGeo.initiate({
                postalCode,
                range: 4,
                timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
              }),
            )
            .unwrap();
          const { timeblocksByRangeWithGeo } = timeblocksByRangeWithGeoQuery;
          return timeblocksByRangeWithGeo.weeks.some((t) => t.timeblocks);
        } catch (error) {
          console.error(error);
          return false;
        }
      },
      message: intl.WE_CANNOT_DELIVER_HERE,
    })
    .required(),
  validatedAddress: yup.object({
    streetName: yup.string(),
    streetNumber: yup.string(),
    postalCode: yup.string(),
    postalArea: yup.string(),
    searchText: yup.string().nullable(),
    longitude: yup.number(),
    latitude: yup.number(),
    letterExtra: yup.string(),
    mode: yup.number(),
    addressName: yup.string(),
    addressCode: yup.string(),
    addressGuid: yup.string(),
    apartments: yup.array().of(
      yup.object({
        name: yup.string(),
        floor: yup.string(),
        door: yup.string().nullable(),
      }),
    ),
    digitalKeyProvider: yup
      .object({
        name: yup.string(),
        addressDigitalKeyProviderId: yup.string(),
      })
      .nullable(),
  }),
  apartment: yup.object({
    name: yup.string(),
    floor: yup.string(),
    door: yup.string().nullable(),
  }),

  setAsNewShippingAddress: yup.boolean(),

  instructionsToDriver: yup.string().nullable(),
  doorCode: yup.string().nullable(),
  name: yup.string(),
  requiresPhysicalKey: yup.boolean().nullable(),

  basketTimeblockId: yup.number(),
  basketDeliveryWeekType: yup.number().nullable(),
});

export type AddressFormValues = yup.InferType<typeof schema>;
